import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Box,
  Typography,
  Chip,
  Divider,
  Badge,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Search as SearchIcon,
  History as HistoryIcon,
  Business as BusinessIcon,
  Timeline as TimelineIcon,
  Campaign as CampaignIcon,
  People as PeopleIcon,
  Message as MessageIcon,
  Logout as LogoutIcon,
  Delete as DeleteIcon,
  CalendarMonth as CalendarMonthIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks';
import { useNotificationContext } from '../../hooks/NotificationContext';

interface CompanySidebarProps {
  open: boolean;
  onClose: () => void;
  variant?: 'permanent' | 'persistent' | 'temporary';
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  category?: string;
  divider?: boolean;
  adminOnly?: boolean;
}

export const CompanySidebar: React.FC<CompanySidebarProps> = ({
  open,
  onClose,
  variant = 'permanent',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { unreadCount } = useNotificationContext();

  const isCompanyAdmin = user?.userRole === 'company_admin';
  const isHR = user?.userRole === 'hr';
  const hasAdminAccess = isCompanyAdmin || isHR; // 企業管理者と人事担当者のみ管理機能にアクセス可能

  // メニュー項目の定義
  const menuItems: MenuItem[] = [
    {
      id: 'dashboard',
      label: 'ダッシュボード',
      icon: <DashboardIcon />,
      path: '/company/dashboard',
      divider: true,
      adminOnly: true,
    },
    // 採用・スカウト
    {
      id: 'student-search',
      label: '学生検索・一覧',
      icon: <SearchIcon />,
      path: '/company/students/search',
      category: 'recruitment',
      adminOnly: true,
    },
    {
      id: 'scout-history',
      label: 'スカウト履歴',
      icon: <HistoryIcon />,
      path: '/company/scout/history',
      category: 'recruitment',
      adminOnly: true,
    },
    {
      id: 'message-inbox',
      label: 'メッセージ管理',
      icon: <MessageIcon />,
      path: '/company/messages',
      category: 'recruitment',
      adminOnly: true,
      divider: true,
    },
    // 企業情報・広報
    {
      id: 'company-profile',
      label: 'プロフィール管理',
      icon: <BusinessIcon />,
      path: '/company/profile',
      category: 'promotion',
      adminOnly: true,
    },
    {
      id: 'timeline-management',
      label: 'タイムライン管理',
      icon: <TimelineIcon />,
      path: '/company/timeline',
      category: 'promotion',
      adminOnly: true,
    },
    {
      id: 'sponsor-settings',
      label: 'スポンサー設定',
      icon: <CampaignIcon />,
      path: '/company/sponsor',
      category: 'promotion',
      adminOnly: true,
    },
    {
      id: 'event-calendar',
      label: 'イベントカレンダー',
      icon: <CalendarMonthIcon />,
      path: '/events',
      category: 'promotion',
      adminOnly: true,
    },
    {
      id: 'company-notifications',
      label: '通知一覧',
      icon: <NotificationsIcon />,
      path: '/company/notifications',
      category: 'promotion',
      adminOnly: true,
      divider: true,
    },
    // 社内管理（管理者のみ）
    {
      id: 'employee-management',
      label: '従業員管理',
      icon: <PeopleIcon />,
      path: '/company/employees',
      category: 'management',
      adminOnly: true,
    },
    {
      id: 'account-deletion',
      label: 'アカウント退会',
      icon: <DeleteIcon />,
      path: '/account-deletion',
      category: 'management',
      divider: true,
    },
  ];

  // 権限に応じてメニューをフィルタリング
  const filteredMenuItems = menuItems.filter((item) => {
    // 企業管理者・人事担当者は全機能にアクセス可能（違いは連動削除のみ）
    if (item.adminOnly && !hasAdminAccess) {
      return false;
    }
    return true;
  });

  // カテゴリ別のラベル
  const categoryLabels = {
    recruitment: '採用・スカウト',
    promotion: '企業情報・広報',
    management: '社内管理',
  };

  const handleItemClick = (path: string) => {
    navigate(path);
    if (variant === 'temporary') {
      onClose();
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/company/login');
    } catch (error) {
      console.error('ログアウトエラー:', error);
    }
  };

  const drawerContent = (
    <Box
      sx={{
        width: 280,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* 企業ロゴ・名前 */}
      <Box sx={{ p: 2, borderBottom: '1px solid #E5E7EB' }}>
        <Typography variant="h6" sx={{ color: '#111827', fontWeight: 'bold' }}>
          企業管理画面
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            {user?.email || 'ユーザー'}
          </Typography>
          <Chip
            label={isCompanyAdmin ? '管理者' : isHR ? '人事' : '従業員'}
            size="small"
            color={isCompanyAdmin ? 'primary' : isHR ? 'secondary' : 'default'}
            variant="outlined"
          />
        </Box>
      </Box>

      {/* メニューリスト */}
      <List sx={{ flexGrow: 1, py: 1 }}>
        {filteredMenuItems.map((item, index) => {
          const prevItem = filteredMenuItems[index - 1];
          const showCategoryHeader =
            item.category && (!prevItem || prevItem.category !== item.category);

          return (
            <React.Fragment key={item.id}>
              {/* カテゴリヘッダー */}
              {showCategoryHeader && (
                <ListSubheader
                  sx={{
                    bgcolor: 'transparent',
                    color: '#6B7280',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    py: 1,
                    px: 2,
                  }}
                >
                  {categoryLabels[item.category as keyof typeof categoryLabels]}
                </ListSubheader>
              )}

              <ListItem disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => handleItemClick(item.path)}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: '#EBF8FF',
                      borderRight: '3px solid #2E5BBA',
                      '& .MuiListItemIcon-root': {
                        color: '#2E5BBA',
                      },
                      '& .MuiListItemText-primary': {
                        color: '#1E3F8A',
                        fontWeight: 600,
                      },
                    },
                    '&:hover': {
                      backgroundColor: '#F3F4F6',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {item.id === 'company-notifications' && unreadCount > 0 ? (
                      <Badge badgeContent={unreadCount} color="error" max={99}>
                        {item.icon}
                      </Badge>
                    ) : item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: location.pathname === item.path ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>

              {/* 区切り線 */}
              {item.divider && <Divider sx={{ my: 1 }} />}
            </React.Fragment>
          );
        })}

        {/* ログアウトボタン */}
        <Divider sx={{ my: 1 }} />
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              mx: 1,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: '#FEF2F2',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: '#DC2626' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="ログアウト"
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#DC2626',
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile.
      }}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          boxSizing: 'border-box',
          borderRight: '1px solid #E5E7EB',
          backgroundColor: '#FFFFFF',
          ...(variant !== 'temporary' && {
            top: '64px', // ヘッダーの高さ分下げる
            height: 'calc(100vh - 64px)', // ヘッダー分を除いた高さ
          }),
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};
